package models

/** One account, reduced to what ordering needs. */
data class AccountRef(
    val accountId: String,
    /** As shown in the list header; null/blank for an account with no institution. */
    val institutionName: String?,
)

/** Accounts belonging to one institution, in the order they should be drawn. */
data class AccountGroup(
    /** The heading. Empty string for the accounts with no institution. */
    val institution: String,
    val accounts: List<AccountRef>,
)

/**
 * THE definition of what order accounts appear in, on every platform.
 *
 * Accounts arrive from the API in whatever order the database returns, which
 * splits an institution's accounts apart — a user with two Wealthsimple accounts
 * sees them separated by their EQ Bank one. Grouping is therefore the default
 * and needs no configuration; [savedOrder] only refines it.
 *
 * A single flat list of account ids drives BOTH levels: a group sorts by the
 * best position any of its accounts holds, and accounts sort within their group.
 * That keeps one thing to persist and makes the two orders incapable of
 * disagreeing — which two separate lists would eventually do.
 *
 * Nothing is ever dropped. An account missing from [savedOrder] — freshly
 * linked, or linked on another device before this one synced — sorts to the end
 * of its group rather than vanishing. That failure mode is not hypothetical:
 * `SettingsManager.getChartOrder` filters a stored order without ever adding to
 * it, and a chart added after the user last reordered silently stopped
 * rendering.
 */
object AccountOrder {

    /** Sorts after every saved position, without overflowing on any platform. */
    private const val UNSAVED = Int.MAX_VALUE

    /**
     * [accounts] arranged into institution groups.
     *
     * Ties — two unsaved accounts, or two groups with no saved accounts — keep
     * their order in [accounts], so an unconfigured list is exactly the API's
     * order with each institution's accounts collected together.
     */
    fun arrange(accounts: List<AccountRef>, savedOrder: List<String>): List<AccountGroup> {
        if (accounts.isEmpty()) return emptyList()

        val position = savedOrder.withIndex().associate { (i, id) -> id to i }
        fun rank(a: AccountRef) = position[a.accountId] ?: UNSAVED

        // Grouped by institution, each group keeping the accounts' arrival order
        // so the sort below only has to break ties it actually cares about.
        val byInstitution = LinkedHashMap<String, MutableList<AccountRef>>()
        for (a in accounts) {
            val key = a.institutionName?.trim().orEmpty()
            byInstitution.getOrPut(key) { mutableListOf() }.add(a)
        }

        // sortedBy is stable, so arrival order survives every tie.
        return byInstitution.entries
            .map { (institution, members) ->
                AccountGroup(institution, members.sortedBy { rank(it) })
            }
            // Two levels, not one. Unnamed institutions sort last ALWAYS — an
            // account with no bank behind it is the odd one out, not the
            // headline — and folding that into the rank instead would only tie
            // it with the other unsaved groups, letting arrival order decide.
            //
            // Then: a group is worth as much as its best-placed account, so
            // dragging one account to the top carries its institution with it.
            // That is the only reading in which grouping and manual order cannot
            // contradict each other.
            .sortedWith(
                compareBy<AccountGroup> { if (it.institution.isEmpty()) 1 else 0 }
                    .thenBy { g -> g.accounts.minOfOrNull { rank(it) } ?: UNSAVED },
            )
    }

    /**
     * [arrange] flattened back to account ids — what a reorder writes back.
     *
     * Always a complete list, so saving it converts every "unsaved, sorted last"
     * account into an explicit position and the order stops drifting as accounts
     * are added.
     */
    fun flatten(groups: List<AccountGroup>): List<String> =
        groups.flatMap { g -> g.accounts.map { it.accountId } }
}
